import { useState, useEffect, useMemo } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { FormModal } from "@/components/form-modal";
import { ParticipantSearchInput } from "@/components/participant-search-input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { performerTypes } from "@shared/schema";
import type { Participant, Performer } from "@shared/schema";

const formSchema = z.object({
  participantId: z.number().optional(),
  name: z.string().min(1, "Participant name is required"),
  notes: z.string().optional(),
  instagram: z.string().optional(),
  performerType: z.enum(performerTypes),
  priority: z.boolean().optional().default(false),
  jamNightId: z.number(),
});

interface AddOpenMicParticipantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jamNightId: number;
}

export default function AddOpenMicParticipantModal({ open, onOpenChange, jamNightId }: AddOpenMicParticipantModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Fetch jam night details to get organisation ID
  const { data: jamNight } = useQuery({
    queryKey: [`/api/events/${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/events/${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Fetch all participants for the organisation
  const { data: participants = [] } = useQuery({
    queryKey: [`/api/participants?organisationId=${jamNight?.organisationId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/participants?organisationId=${jamNight?.organisationId}`);
      return response.json();
    },
    enabled: open && !!jamNight?.organisationId,
  });

  // Fetch existing performers for this event to check for duplicates
  const { data: existingPerformers = [] } = useQuery({
    queryKey: [`/api/performers?jamNightId=${jamNightId}`],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/performers?jamNightId=${jamNightId}`);
      return response.json();
    },
    enabled: open,
  });

  // Get set of participant IDs that are already added to this event
  const existingParticipantIds = useMemo(() => {
    return new Set<number>(
      existingPerformers
        .filter((p: Performer) => p.participantId && !p.removed)
        .map((p: Performer) => p.participantId)
    );
  }, [existingPerformers]);

  // Filter participants based on search term
  const filteredParticipants = useMemo(() => {
    if (!participants) return [];
    
    if (searchTerm.trim().length === 0) {
      return participants.sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
    }
    
    return participants
      .filter((participant: Participant) => 
        participant.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a: Participant, b: Participant) => a.name.localeCompare(b.name));
  }, [participants, searchTerm]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      participantId: undefined,
      name: "",
      notes: "",
      instagram: "",
      performerType: "solo",
      priority: false,
      jamNightId,
    },
  });

  const createPerformerMutation = useMutation({
    mutationFn: async (performerData: z.infer<typeof formSchema>) => {
      const response = await apiRequest("POST", "/api/performers", performerData);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/performers?jamNightId=${jamNightId}`] });
      // Also invalidate participants query to update autocomplete
      if (jamNight?.organisationId) {
        queryClient.invalidateQueries({ queryKey: [`/api/participants?organisationId=${jamNight.organisationId}`] });
      }
      form.reset();
      setSelectedParticipant(null);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      // Check if the error is a duplicate participant error
      if (error.message === "This participant is already added to this event") {
        toast({
          title: "Duplicate Participant",
          description: "This participant is already added to this event",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to add participant",
          variant: "destructive",
        });
      }
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    createPerformerMutation.mutate(data);
  };

  const handleParticipantSelect = (participant: Participant) => {
    setSelectedParticipant(participant);
    form.setValue("participantId", participant.id);
    form.setValue("name", participant.name);
    // Populate Instagram if available
    if (participant.instagram) {
      form.setValue("instagram", participant.instagram);
    }

    // Fetch and pre-populate performer settings from most recent performer record
    const fetchRecentPerformerSettings = async () => {
      try {
        const response = await apiRequest("GET", `/api/performers/recent/${participant.id}`);
        const recentPerformer = await response.json();
        
        if (recentPerformer) {
          // Pre-populate performer type if it exists
          if (recentPerformer.performerType) {
            form.setValue("performerType", recentPerformer.performerType);
          }
          // Pre-populate priority
          form.setValue("priority", recentPerformer.priority || false);
        }
      } catch (error: Error | unknown) {
        // apiRequest throws for 404, which is expected for participants with no recent performer
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes("404")) {
          // No recent performer found, using defaults - this is expected
        }
      }
    };

    fetchRecentPerformerSettings();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    form.setValue("name", value);
  };

  const handleClearSelection = () => {
    setSelectedParticipant(null);
    setSearchTerm("");
    form.setValue("participantId", undefined);
    form.setValue("name", "");
    form.setValue("notes", "");
    form.setValue("instagram", "");
    form.setValue("performerType", "solo");
    form.setValue("priority", false);
  };


  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSelectedParticipant(null);
      setSearchTerm("");
      form.reset();
    }
  }, [open, form]);

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Add Open Mic Participant"
      form={form}
      onSubmit={onSubmit}
      isLoading={createPerformerMutation.isPending}
      submitLabel={createPerformerMutation.isPending ? "Adding..." : "Add Participant"}
      width="md"
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Participant Name</FormLabel>
            <FormControl>
              <ParticipantSearchInput
                participants={filteredParticipants}
                existingParticipantIds={existingParticipantIds}
                value={searchTerm}
                onSearchChange={handleSearchChange}
                onParticipantSelect={handleParticipantSelect}
                onClear={handleClearSelection}
                placeholder="Search for participant or enter new name..."
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

            <FormField
              control={form.control}
              name="performerType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Performer Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select performer type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="solo">Solo</SelectItem>
                      <SelectItem value="band">Band</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Priority Participant</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this participant as priority
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instagram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram (Optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="@username or instagram.com/username" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Add any notes about this participant..."
                      className="min-h-[80px] resize-none"
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

    </FormModal>
  );
}